import { Download, FileSpreadsheet } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { useToast } from '../components/ui/Toast';
import { usePermission } from '../auth/usePermission';
import { Permission } from '../auth/permissions';

/**
 * Exports comptables — the FEC download (client question 5, 2026-10-23). The file existed only as
 * an API route (GET /invoices/exports/fec, ACCOUNTING_EXPORT: owner + accounting); this page had
 * been a placeholder that printed that path. A whole fiscal year, or any date range (end date
 * inclusive here — the API's `to` is exclusive, so the day after is sent).
 */
export default function AccountingExports() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const allowed = usePermission(Permission.ACCOUNTING_EXPORT);
  const thisYear = new Date().getFullYear();
  const [mode, setMode] = useState<'YEAR' | 'RANGE'>('YEAR');
  const [year, setYear] = useState(String(thisYear));
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);

  const dayAfter = (d: string) => {
    const x = new Date(`${d}T00:00:00Z`);
    x.setUTCDate(x.getUTCDate() + 1);
    return x.toISOString().slice(0, 10);
  };
  const rangeOk = mode === 'YEAR' || (from !== '' && to !== '' && from <= to);

  const download = async () => {
    setBusy(true);
    try {
      const params = mode === 'YEAR' ? { year } : { from, to: dayAfter(to) };
      const res = await apiClient.get('/invoices/exports/fec', { params, responseType: 'blob' });
      const name = mode === 'YEAR' ? `FEC_${year}.txt` : `FEC_${from}_${dayAfter(to)}.txt`;
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      push({ tone: 'success', message: t('accountingExports.downloaded', { name }) });
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={t('accountingExports.title')}
        description={t('accountingExports.description')}
      />
      <div className="rounded-lg border bg-card p-5 shadow-sm">
        <div className="mb-4 flex items-center gap-2">
          <FileSpreadsheet className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-base font-semibold">{t('accountingExports.fecTitle')}</h2>
        </div>
        <p className="mb-4 max-w-3xl text-sm text-muted-foreground">
          {t('accountingExports.fecIntro')}
        </p>
        {!allowed ? (
          <p className="text-sm text-destructive">{t('accountingExports.notAllowed')}</p>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t('accountingExports.period')}
              </span>
              <Select
                className="w-48"
                value={mode}
                onChange={(e) => setMode(e.target.value as 'YEAR' | 'RANGE')}
              >
                <option value="YEAR">{t('accountingExports.wholeYear')}</option>
                <option value="RANGE">{t('accountingExports.range')}</option>
              </Select>
            </label>
            {mode === 'YEAR' ? (
              <label className="block">
                <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {t('accountingExports.year')}
                </span>
                <Select className="w-32" value={year} onChange={(e) => setYear(e.target.value)}>
                  {[thisYear, thisYear - 1, thisYear - 2].map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Select>
              </label>
            ) : (
              <>
                <label className="block">
                  <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {t('accountingExports.from')}
                  </span>
                  <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    {t('accountingExports.to')}
                  </span>
                  <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                </label>
              </>
            )}
            <Button onClick={() => void download()} disabled={busy || !rangeOk}>
              <Download className="h-4 w-4" />
              {busy ? t('accountingExports.preparing') : t('accountingExports.download')}
            </Button>
          </div>
        )}
        <ul className="mt-5 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
          <li>{t('accountingExports.noteFormat')}</li>
          <li>{t('accountingExports.noteJournals')}</li>
          <li>{t('accountingExports.noteWho')}</li>
        </ul>
      </div>
    </div>
  );
}
